import React, { useEffect, useRef } from 'react';

const EUGlobe3D = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Globe parameters
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) * 0.35;
    let rotation = 0;

    // Cities with their coordinates (simplified 2D projection)
    const cities = [
      { name: 'Thessaloniki', x: 0.3, y: -0.2, color: '#00ffff', size: 8 },
      { name: 'Berlin', x: 0.1, y: -0.4, color: '#3b82f6', size: 6 },
      { name: 'Chemnitz', x: 0.15, y: -0.35, color: '#3b82f6', size: 5 },
      { name: 'Bologna', x: 0.2, y: -0.15, color: '#8b5cf6', size: 5 },
      { name: 'Vienna', x: 0.25, y: -0.3, color: '#8b5cf6', size: 5 },
      { name: 'Brussels', x: 0, y: -0.35, color: '#eab308', size: 6 }
    ];

    // Particles for stars/background
    const particles = [];
    for (let i = 0; i < 100; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5,
        opacity: Math.random() * 0.5 + 0.3
      });
    }

    function drawGlobe() {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw particles (stars)
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${p.opacity})`;
        ctx.fill();
      });

      // Draw outer glow
      const gradient = ctx.createRadialGradient(centerX, centerY, radius * 0.8, centerX, centerY, radius * 1.2);
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
      gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 1.2, 0, Math.PI * 2);
      ctx.fill();

      // Draw main globe circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw latitude lines
      for (let i = -2; i <= 2; i++) {
        const y = centerY + (i * radius / 3);
        const width = Math.sqrt(radius * radius - (i * radius / 3) * (i * radius / 3)) * 2;
        
        ctx.beginPath();
        ctx.ellipse(centerX, y, width / 2, width / 10, 0, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.15)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Draw longitude lines
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2 + rotation;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radius * Math.abs(Math.cos(angle)), radius, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(59, 130, 246, ${0.1 + Math.abs(Math.cos(angle)) * 0.1})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Draw cities
      cities.forEach((city, index) => {
        const rotatedX = city.x * Math.cos(rotation) - city.y * Math.sin(rotation);
        const rotatedY = city.x * Math.sin(rotation) + city.y * Math.cos(rotation);
        
        const px = centerX + rotatedX * radius;
        const py = centerY + rotatedY * radius;
        
        // City glow
        const cityGradient = ctx.createRadialGradient(px, py, 0, px, py, city.size * 3);
        cityGradient.addColorStop(0, city.color);
        cityGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = cityGradient;
        ctx.beginPath();
        ctx.arc(px, py, city.size * 3, 0, Math.PI * 2);
        ctx.fill();
        
        // City dot
        ctx.beginPath();
        ctx.arc(px, py, city.size, 0, Math.PI * 2);
        ctx.fillStyle = city.color;
        ctx.fill();
        
        // Pulsing effect
        const pulse = Math.sin(Date.now() / 1000 + index) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(px, py, city.size + pulse * 4, 0, Math.PI * 2);
        ctx.strokeStyle = city.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 1 - pulse;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      // Draw connection lines from Thessaloniki to other cities
      const thess = cities[0];
      const thessX = centerX + (thess.x * Math.cos(rotation) - thess.y * Math.sin(rotation)) * radius;
      const thessY = centerY + (thess.x * Math.sin(rotation) + thess.y * Math.cos(rotation)) * radius;
      
      cities.slice(1).forEach(city => {
        const rotatedX = city.x * Math.cos(rotation) - city.y * Math.sin(rotation);
        const rotatedY = city.x * Math.sin(rotation) + city.y * Math.cos(rotation);
        const px = centerX + rotatedX * radius;
        const py = centerY + rotatedY * radius;
        
        ctx.beginPath();
        ctx.moveTo(thessX, thessY);
        ctx.lineTo(px, py);
        ctx.strokeStyle = city.color;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.3;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      rotation += 0.002;
    }

    // Animation loop
    let animationId;
    function animate() {
      drawGlobe();
      animationId = requestAnimationFrame(animate);
    }
    animate();

    // Handle resize
    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.8 }}
    />
  );
};

export default EUGlobe3D;
